import calculateTextWidth from '../../utilities/calculateTextWidth';
import calcWidthFromCharCount from './calcWidthFromCharCount';
import PlaylistColumns, { ColumnKey, ColumnScope, ColumnSettings } from './playlistColumns';

/** The narrowest each column may be shown, in pixels. */
export type ColumnMinimums = { [K in ColumnKey]?: number };

/**
 * Fits a saved layout to the viewport it is being shown in.
 *
 * A layout is pixels, saved together with the width of the viewport they
 * were set in (the reference). Shown in a viewport of another width, the
 * columns sized from a character count - Time, Year, the ones that hold a
 * value of known length - keep their pixels, and the rest share whatever
 * is left in proportion to the shares they had at the reference. So the
 * Name column, which was 40% of the free space, is still 40% of the free
 * space; and a user who dragged Artist to twice Album keeps that too.
 *
 * Proportion alone was tried and found wanting: a column that was already
 * narrow shrinks into nothing. So every column has a minimum, and this
 * does what flexbox does with min-width: a column that would fall under
 * its minimum is pinned there, taken out of the pool, and the shortfall
 * is spread over the rest. That repeats until nothing more pins. When
 * everything is pinned the layout is wider than the viewport and the
 * table scrolls sideways, which is the right time for a scrollbar - the
 * alternative, dropping columns, was judged not worth it for a case this
 * far into the corner.
 *
 * Pure, and returns `saved` itself when there is nothing to do, so memos
 * downstream keep their identity through a resize that changes nothing.
 * The reference is unknown for a layout saved before there was one; that
 * is fitted as-is until something re-saves it with a reference.
 */
export default function fitColumns(
  saved: Partial<ColumnSettings>,
  referenceWidth: number | undefined,
  viewportWidth: number | undefined,
  minimums: ColumnMinimums,
): Partial<ColumnSettings> {
  if (referenceWidth === undefined || viewportWidth === undefined) {
    return saved;
  }

  if (Math.abs(referenceWidth - viewportWidth) < 0.5) {
    return saved;
  }

  const keys = Object.keys(saved) as ColumnKey[];

  // Hidden columns are width 0 and stay that way; they count as fixed at 0.
  const isFlexible = (key: ColumnKey) =>
    saved[key]!.width > 0 && PlaylistColumns[key]?.calcFromCharCount !== true;

  let fixedSum = 0;

  for (const key of keys) {
    if (!isFlexible(key)) {
      fixedSum += saved[key]!.width;
    }
  }

  const pool = new Set(keys.filter(isFlexible));

  // No free space at the reference means the flexible columns' shares are
  // meaningless, and nothing to scale them from.
  if (pool.size === 0 || referenceWidth - fixedSum <= 0) {
    return saved;
  }

  let remaining = Math.max(0, viewportWidth - fixedSum);
  const widths: { [K in ColumnKey]?: number } = {};

  // Pin anything that would fall under its minimum, then share what is
  // left among the rest by their saved shares; repeat until nothing pins.
  // `remaining` only ever goes down, so a column that pins in a pass would
  // pin in any later one as well - the order of discovery does not matter.
  let pinned = true;

  while (pinned) {
    pinned = false;

    const poolSaved = sumWidths(saved, pool);

    for (const key of pool) {
      const min = minimums[key] || 0;
      const share = poolSaved > 0 ? (saved[key]!.width / poolSaved) * remaining : 0;

      if (share < min) {
        widths[key] = min;
        remaining -= min;
        pool.delete(key);
        pinned = true;
      }
    }
  }

  const poolSaved = sumWidths(saved, pool);

  for (const key of pool) {
    widths[key] = (saved[key]!.width / poolSaved) * remaining;
  }

  const fitted: Partial<ColumnSettings> = { ...saved };

  for (const key of Object.keys(widths) as ColumnKey[]) {
    (fitted as any)[key] = { ...saved[key]!, width: widths[key]! };
  }

  return fitted;
}

function sumWidths(settings: Partial<ColumnSettings>, keys: Iterable<ColumnKey>) {
  let sum = 0;

  for (const key of keys) {
    sum += settings[key]!.width;
  }

  return sum;
}

/**
 * How narrow each column may go in a scope, measured in the current font.
 *
 * A flexible column's floor is its `minChars` - enough of a name to tell
 * what it is - and a character-count column's floor is the count it is
 * sized from, so Time can never be narrower than a time. The header label
 * is a floor for both: it is the sort target, and it should stay
 * clickable. Needs a document to measure in, so it is not called from the
 * pure fit.
 */
export function measureColumnMinimums(scope: ColumnScope): ColumnMinimums {
  const minimums: ColumnMinimums = {};

  for (const key of Object.keys(PlaylistColumns) as ColumnKey[]) {
    const column = PlaylistColumns[key];

    const chars = column.calcFromCharCount ? column.defaultWidthPerc[scope] : column.minChars || 0;

    minimums[key] = Math.max(
      chars > 0 ? calcWidthFromCharCount(chars) : 0,
      calculateTextWidth(column.name),
    );
  }

  return minimums;
}
